import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "booking-panel" }
const _hoisted_2 = { class: "booking-panel__title booking-panel__title--calendar" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", {
  class: "icon icon-calendar me-2 text-primary booking-panel__title-icon",
  "aria-hidden": "true"
}, null, -1)
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  key: 0,
  class: "me-4 d-block d-md-inline-block"
}
const _hoisted_6 = {
  key: 1,
  class: "d-block d-md-inline-block"
}
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "booking-datepicker" }
const _hoisted_9 = ["onClick", "disabled"]
const _hoisted_10 = /*#__PURE__*/_createElementVNode("span", { class: "icon icon-arrow-right icon-rotate-180" }, null, -1)
const _hoisted_11 = [
  _hoisted_10
]
const _hoisted_12 = { class: "booking-datepicker__month-header" }
const _hoisted_13 = ["onClick", "disabled"]
const _hoisted_14 = /*#__PURE__*/_createElementVNode("span", { class: "icon icon-arrow-right" }, null, -1)
const _hoisted_15 = [
  _hoisted_14
]

import {ref, watch, computed} from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import {useMediaQuery} from '@vueuse/core';
import {storeToRefs} from 'pinia';
import {useBookingDataStore} from "../stores/BookingDataStore";
import {locales} from "../utils/utils";
import {localDateToUTCDate, UTCDateToLocalDate, ISOStringToDate} from "../../libs/@elements/date-utils";
import {trans as translate} from '../plugins/translate';


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarView',
  setup(__props) {

const isMobile = useMediaQuery('(max-width: 767px)');

const bookingDataStore = useBookingDataStore();
const {startDate, endDate, nextButtonDisabled} = storeToRefs(bookingDataStore);

const configLang = window._config.lang;

const tempMinDate = ref(null);
const tempMaxDate = ref(null);


const toLocalISOString = (date) => {
    // format date to match closedDates format. e.g. '2023-06-13'
    // note: toISOString is one day of
    // note: closed-dates should be converted instead and compared against date
    //let isoDateLocal = date.toISOString().split('T')[0];

    // from utc
    let parts = [
        date.getFullYear(),
        (date.getMonth() + 1).toString().padStart( 2, '0'),
        date.getDate().toString().padStart( 2, '0 ')
    ];
    return parts.join( '-');
};

const isDateDisabled = (date) => {
    if (bookingDataStore.selectedLocation.availableRanges.length && !_isDateInRange(date, bookingDataStore.selectedLocation.availableRanges)) {
        // out of seasonal bounds
        return true;
    }
    // day closed?
    if (bookingDataStore.selectedLocation.closedDates.length) {
        let isoDateLocal = toLocalISOString( date );
        return bookingDataStore.selectedLocation.closedDates.includes(isoDateLocal);
    }
    return false;
};

function _isDateInRange( date, ranges ) {
    let contained = true;
    if (ranges.length) {
        contained = false;
        for (let i = 0; i < ranges.length; i++) {
            // date (param) comes from the datepicker, so it's always 00:00:00 current timezone
            // ranges are date strings without time, so we need to add the time to make it comparable ->
            // if we add the time, it's 00:00:00 in the current timezone. Adding Z at the end makes it UTC, and we don't want UTC
            if (date >= new Date(ranges[i].start + 'T00:00:00') && date <= new Date(ranges[i].end + 'T00:00:00')) {
                contained = true;
                break;
            }
        }
        return contained;
    }
    return contained;
}

const getRestrictedRange = function (fromDate) {
    // important: make sure fromDate is a date in the current timezone
    if (!fromDate) {
        fromDate = startDate.value;
    }

    let fromDateUTC = localDateToUTCDate(fromDate); // convert to UTC to make it comparable in ranges
    // this gets the range from the min-to-date until max-to-date (which is selected date + maxDuration)
    let restrictedRange = {
        start: fromDateUTC,
        end: addDays(fromDateUTC, bookingDataStore.selectedLocation.durationMax)
    };

    let availableRanges = bookingDataStore.selectedLocation.availableRanges
    if (availableRanges.length) {
        // season-ranges + sub-seasons, will prevent end-date selection over boundaries

        // if the overall max-date is lower than the range end, use the overall max-date
        if (new Date(bookingDataStore.selectedLocation.maxDate + 'T00:00:00Z') < restrictedRange.end) {
            restrictedRange.end = new Date(bookingDataStore.selectedLocation.maxDate + 'T00:00:00Z');
        }

        // set restricted range to the overall max-date
        let restrictedMin = restrictedRange.start;
        let restrictedMax = restrictedRange.end;


        // get the range that contains the fromDate
        let containedInRanges = availableRanges.filter( function( currentAvailableRange ) {
            // isDateInRange wants a date in current timezone, so use fromDate, not fromdateUTC
            return _isDateInRange( fromDate, [currentAvailableRange]);
        } );

        // if there is a range that contains the fromDate, use this range to restrict the max-date even more, if needed
        for(let i = 0; i < containedInRanges.length; i++) {
            let currentRange = containedInRanges[i];

            if (new Date(currentRange.end + 'T00:00:00Z') < restrictedMax) {
                restrictedMax = new Date(currentRange.end);
            }
        }

        restrictedRange = {start: UTCDateToLocalDate(restrictedMin), end: UTCDateToLocalDate(restrictedMax)};

    }
    return restrictedRange;
};

// this checks if the button that shows the next or previous month has to be disabled
const isButtonDisabled = function(month, year, next) {
    let disabled = false;

    if (next) {
        // if the first day of the next month is after the max-date, disable the button
        let dateToCompare = new Date(year, month +1, 1);
        if (dateToCompare > UTCDateToLocalDate(new Date(tempMaxDate.value || bookingDataStore.selectedLocation.maxDate))) {
            disabled = true;
        }
    } else {
        // if the first day of the current month is before or equal to the min-date, disable the button
        let dateToCompare = new Date(year, month, 1);
        if (dateToCompare <= UTCDateToLocalDate(new Date(tempMinDate.value || bookingDataStore.selectedLocation.minDate))) {
            disabled = true;
        }
    }
    return disabled;

}

const addDays = function (date, days) {
    let mutated = new Date(date);
    mutated.setDate(mutated.getDate() + days);
    return mutated;
};

const handleStartDate = (value) => {
    startDate.value = value;
    endDate.value = null;
    // if this date is in a season (range), set max date to the end of the season temporarily (until the booking range is selected)
    let restrictedRange = getRestrictedRange(value);
    tempMinDate.value = restrictedRange.start;
    tempMaxDate.value = restrictedRange.end;
    // the button doesn't need to be disabled - if only a start date is selected, the end date will be set to the start date on next click
    nextButtonDisabled.value = false;
};

const handleEndDate = (value) => {
    endDate.value = value;
    // reset to standard ranges (remove temporary range)
    tempMinDate.value = null;
    tempMaxDate.value = null;
    nextButtonDisabled.value = false;
};

watch(
    () => bookingDataStore.date,
    (newDateArray) => {
        // the datepicker sorts the dates, so assign them in the correct order
        if (!bookingDataStore.selectedLocation.dateValidationError && newDateArray?.length === 2) {
            startDate.value = new Date(newDateArray[0]);
            endDate.value = new Date(newDateArray[1]);
            nextButtonDisabled.value = false;
        }
    },
    {deep: true, immediate: true}
)


const markers = computed(() => {
    let markers = [];
    // add markers for all the closed dates
    if (bookingDataStore.selectedLocation.closedDates.length) {
        bookingDataStore.selectedLocation.closedDates.forEach((closedDate) => {
            markers.push({
                date: UTCDateToLocalDate(ISOStringToDate(closedDate)),
                type: 'dot',
                tooltip: [{text: translate('rent_site.location.rental-period.tooltip.shop-closed')}],
                color: '#a7a7a6',
            });

        });
    }

    // add markers for the available ranges (start and end)
    // if (bookingDataStore.selectedLocation.availableRanges.length) {
    //     bookingDataStore.selectedLocation.availableRanges.forEach((range) => {
    //         // mark season start
    //         markers.push({
    //             date: new Date(range.start),
    //             type: 'line',
    //             tooltip: [{text: translate('rent_site.location.rental-period.tooltip.season-start')}],
    //             color: '#a7a7a6',
    //         });
    //
    //         // mark season end
    //         markers.push({
    //             date: new Date(range.end),
    //             type: 'line',
    //             tooltip: [{text: translate('rent_site.location.rental-period.tooltip.season-end')}],
    //             color: '#a7a7a6',
    //         });
    //     });
    // }

    return markers;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      (_unref(startDate) || _unref(endDate))
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
            (_unref(startDate))
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_ctx.$translate('rent_site.location.rental-period.pickup-date')) + " " + _toDisplayString(_unref(bookingDataStore).formattedStartDate), 1))
              : _createCommentVNode("", true),
            _createTextVNode(),
            (_unref(endDate))
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_ctx.$translate('rent_site.location.rental-period.return-date')) + " " + _toDisplayString(_unref(bookingDataStore).formattedEndDate), 1))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_ctx.$translate('rent_site.location.booking-widget.rental-period.label')), 1))
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_unref(VueDatePicker), {
        modelValue: _unref(bookingDataStore).date,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(bookingDataStore).date) = $event)),
        vertical: _unref(isMobile),
        "auto-apply": "",
        range: "",
        locale: _unref(locales)[_unref(configLang)],
        "week-start": (_unref(configLang) === 'en' || _unref(configLang) === 'fr') ? 0 : 1,
        "multi-calendars": _unref(isMobile) ? 12 : 2,
        inline: "",
        "month-change-on-scroll": false,
        "no-swipe": "",
        "month-name-format": "long",
        "min-date": tempMinDate.value || _unref(UTCDateToLocalDate)(_unref(ISOStringToDate)(_unref(bookingDataStore).selectedLocation.minDate)),
        "max-date": tempMaxDate.value || _unref(UTCDateToLocalDate)(_unref(ISOStringToDate)(_unref(bookingDataStore).selectedLocation.maxDate)),
        "max-range": _unref(bookingDataStore).selectedLocation.durationMax,
        "prevent-min-max-navigation": "",
        "ignore-time-validation": "",
        "enable-time-picker": false,
        "disabled-dates": isDateDisabled,
        onRangeStart: handleStartDate,
        onRangeEnd: handleEndDate,
        "hide-offset-dates": "",
        markers: markers.value,
        "focus-start-date": _unref(isMobile),
        "start-date": _unref(UTCDateToLocalDate)(_unref(ISOStringToDate)(_unref(bookingDataStore).selectedLocation.minDate))
      }, {
        "month-year": _withCtx(({
                              month,
                              year,
                              months,
                              years,
                              updateMonthYear,
                              handleMonthYearChange,
                              instance
                            }) => [
          (!_unref(isMobile) && instance === 0)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "btn btn-white btn-icon booking-datepicker__arrow booking-datepicker__arrow--prev",
                "aria-label": "previous month",
                title: "previous month",
                onClick: ($event: any) => (handleMonthYearChange(false, true)),
                disabled: isButtonDisabled(month, year, false)
              }, _hoisted_11, 8, _hoisted_9))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("span", null, _toDisplayString(months[month].text) + " " + _toDisplayString(year), 1)
          ]),
          (!_unref(isMobile) && instance === 1)
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "btn btn-white btn-icon booking-datepicker__arrow booking-datepicker__arrow--next",
                "aria-label": "next month",
                title: "next month",
                onClick: ($event: any) => (handleMonthYearChange(true, true)),
                disabled: isButtonDisabled(month, year, true)
              }, _hoisted_15, 8, _hoisted_13))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "vertical", "locale", "week-start", "multi-calendars", "min-date", "max-date", "max-range", "markers", "focus-start-date", "start-date"])
    ])
  ]))
}
}

})